/** Iklan HQ — opted-in sellers advertised on HQ surfaces. */
import { DIRECT_CHECKOUT_PARAM, SALES_PAGE_PARAM } from "./productLink";
import { AD_TICKET_PARAM } from "./adCoupon";

/**
 * Where HQ shows ads. lepasbeli: the thank-you page just after paying. jejak:
 * the same page, come back to track a parcel. testimoni: after rating a
 * product. sijil, akaun, lms, rakaman: the buyer's certificate, account hub,
 * course and class recordings. emel: cards in the purchase email, never
 * charged (an email open is not an impression we can see). komuniti: a post in
 * the buyer's community feed. live, fyp, jelajah: public pages anyone may be
 * watching (a store's Live and FYP feed, Sellhiz Jelajah). checkout: the coupon
 * bump on an HQ store's own checkout (a coupon, claimed only once paid). kedai:
 * an HQ store's product archive (/shop/<store>), public (113M S5).
 */
export const IKLAN_PLACEMENTS = [
  "lepasbeli",
  "jejak",
  "testimoni",
  "sijil",
  "akaun",
  "lms",
  "rakaman",
  "kelas",
  "emel",
  "komuniti",
  "live",
  "fyp",
  "jelajah",
  "checkout",
  "affiliate",
  "kedai",
  "pembantu",
] as const;
export type IklanPlacement = (typeof IKLAN_PLACEMENTS)[number];
/** Sellhiz Jelajah belongs to no store; HQ switches its ads on by listing this id among its stores. */
export const JELAJAH_HOST = "jelajah";
export const JELAJAH_NAME = "Sellhiz Jelajah";
/** FYP awam belongs to no store either: the feed of advertisers' videos HQ's Meta ads land on (S13). */
export const AWAM_HOST = "awam";
export const AWAM_NAME = "FYP awam Sellhiz";
/** The affiliate portal belongs to no store either (111D S7): its ads offer programmes to join, paid per join. */
export const AFFILIATE_HOST = "affiliate";
export const AFFILIATE_NAME = "Portal affiliate Sellhiz";
/** What an advertiser pays for each affiliate who joins from its ad, unless HQ set another (RM). */
export const DEFAULT_AFFILIATE_CPA = 2;
/** Pembantu Sellhiz, the seller's dashboard helper (117C S7): its ads speak to sellers, never to buyers. */
export const PEMBANTU_HOST = "pembantu";
export const PEMBANTU_NAME = "Pembantu Sellhiz";
/** What Pembantu shows unless HQ picks other categories: tools and courses for running a business. */
export const PEMBANTU_DEFAULT_CATEGORIES: readonly IklanCategory[] = ["bisnes", "kursus-bisnes"];
/** One Mesej Ditaja per seller in this many days. */
export const DITAJA_EVERY_DAYS = 7;
/** Hosts that belong to no store: HQ lists them among its stores beside real store ids. */
export const PSEUDO_HOST_IDS = [JELAJAH_HOST, AWAM_HOST, AFFILIATE_HOST, PEMBANTU_HOST] as const;
/** Hosts that belong to no store, with the name HQ's screens give them. */
export const PSEUDO_HOSTS: Record<string, string> = {
  [JELAJAH_HOST]: JELAJAH_NAME,
  [AWAM_HOST]: AWAM_NAME,
  [AFFILIATE_HOST]: AFFILIATE_NAME,
  [PEMBANTU_HOST]: PEMBANTU_NAME,
};
/** How many advertisers' videos one FYP awam feed carries after the one it landed on. */
export const FYP_AWAM_SLOTS = 12;

/** FYP awam, opening on one ad's video when `code` names it: the address a Meta ad sends people to. */
export function fypAwamPath(code?: string | null): string {
  return code ? `/awam/${code}` : "/awam";
}

/** A real store's id, as opposed to Jelajah's: only these may go into a store query. */
import type { StoreCredit } from "./iklanKredit";

export function isStoreId(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

/**
 * Where on a surface an ad sat: one placement can carry several (a community
 * feed has its rail line, a post, a comment and the side card). A card names
 * its slot in `data-slot`; impressions and taps are also counted per slot, so
 * HQ sees which spot sells. Billing stays per placement.
 */
export const IKLAN_SLOTS = [
  "grid",
  "post",
  "rail",
  "kanan",
  "komen",
  "sijil",
  "emel",
  "shop",
  "hujung",
  "ulasan",
  "bump",
  "jalur",
  "jeda",
  "chat",
  "tepi",
  "beg",
  "video",
  "peluang",
  "halaman",
  "produk",
  "kongsi",
  "tiket",
  "bilik",
  "kupon",
  "arkib",
  "pembelian",
  "sejarah",
  "nilai",
  "kepala",
  "hantar",
  "ditaja",
] as const;
export type IklanSlot = (typeof IKLAN_SLOTS)[number];

/**
 * Every spot each placement shows an ad in (113M S4): what HQ switches on and
 * off, and what an ad may be kept to. The surfaces name these in `data-slot`.
 */
export const PLACEMENT_SLOTS: Record<IklanPlacement, readonly IklanSlot[]> = {
  lepasbeli: ["kupon", "grid"],
  jejak: ["kupon", "grid"],
  testimoni: ["kupon", "nilai"],
  sijil: ["sijil"],
  akaun: ["kupon", "grid", "pembelian", "sejarah"],
  lms: ["kupon", "grid"],
  rakaman: ["kupon", "grid"],
  kelas: ["bilik", "chat", "jalur", "jeda"],
  emel: ["emel"],
  komuniti: ["post", "komen", "rail", "kanan", "kongsi"],
  live: ["beg", "komen", "tepi", "grid"],
  fyp: ["video", "hujung", "komen", "kongsi", "shop", "ulasan"],
  jelajah: ["grid"],
  checkout: ["bump"],
  affiliate: ["peluang", "halaman", "produk", "kongsi"],
  kedai: ["arkib"],
  // Pembantu: a strip under the chat's header, one above the composer, and Mesej Ditaja in its inbox.
  pembantu: ["kepala", "hantar", "ditaja"],
};

/** One spot as settings and targeting store it: "komuniti:post". */
export const spotKey = (placement: string, slot: string) => `${placement}:${slot}`;
export const ALL_SPOTS: string[] = IKLAN_PLACEMENTS.flatMap((p) => PLACEMENT_SLOTS[p].map((s) => spotKey(p, s)));
const SPOT_SET = new Set(ALL_SPOTS);

/** Known spot keys only, once each, in catalogue order. */
export function cleanSpots(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const want = new Set(raw.filter((k): k is string => typeof k === "string" && SPOT_SET.has(k)));
  return ALL_SPOTS.filter((k) => want.has(k));
}

/**
 * HQ's placements (Monetize › Placement). Auto, the default, runs every spot and
 * lets the auction learn where each ad sells (Meta's Advantage+ placements);
 * Manual runs only the spots HQ left on. The coupon bump on HQ's own checkout
 * is its own switch, off by default: it sits in the HQ store's own funnel.
 */
export interface PlacementControl {
  mode: "auto" | "manual";
  /** Spots switched off in Manual; kept while in Auto so switching back restores them. */
  off: string[];
  /** The coupon bump on an HQ store's checkout. */
  bump: boolean;
}

export const DEFAULT_PLACEMENT_CONTROL: PlacementControl = { mode: "auto", off: [], bump: false };

export function readPlacementControl(raw: unknown): PlacementControl {
  const o = (raw && typeof raw === "object" ? raw : {}) as Partial<PlacementControl>;
  return { mode: o.mode === "manual" ? "manual" : "auto", off: cleanSpots(o.off), bump: o.bump === true };
}

/** Whether HQ runs ads in this spot now. */
export function spotOn(c: PlacementControl, placement: IklanPlacement, slot: IklanSlot): boolean {
  if (placement === "checkout") return c.bump;
  return c.mode === "auto" || !c.off.includes(spotKey(placement, slot));
}

/** Whether any of a placement's spots run. */
export function placementOn(c: PlacementControl, placement: IklanPlacement): boolean {
  return PLACEMENT_SLOTS[placement].some((s) => spotOn(c, placement, s));
}

/** A placement's spots HQ switched off, for its page to leave empty. */
export function slotsOff(c: PlacementControl, placement: IklanPlacement): IklanSlot[] {
  return PLACEMENT_SLOTS[placement].filter((s) => !spotOn(c, placement, s));
}

/**
 * An ad's own placements (Monetize › Pengiklan): null runs it wherever HQ does
 * (Auto); a list keeps it to those spots (Manual).
 */
export function offerRunsIn(spots: string[] | null, placement: IklanPlacement): boolean {
  return !spots || PLACEMENT_SLOTS[placement].some((s) => spots.includes(spotKey(placement, s)));
}

/** Whether an ad can show anywhere in a placement: a spot HQ runs that the ad is not kept off. */
export function offerCanShow(spots: string[] | null, c: PlacementControl, placement: IklanPlacement): boolean {
  return PLACEMENT_SLOTS[placement].some((s) => spotOn(c, placement, s) && (!spots || spots.includes(spotKey(placement, s))));
}

/** The spots of a placement an ad is kept to; null when it may sit in any. */
export function offerSlots(spots: string[] | null, placement: IklanPlacement): IklanSlot[] | null {
  if (!spots) return null;
  const mine = PLACEMENT_SLOTS[placement].filter((s) => spots.includes(spotKey(placement, s)));
  return mine.length === PLACEMENT_SLOTS[placement].length ? null : mine;
}

/** An ad's placements as stored in D1 (iklan_offers.placements): JSON, or null for Auto. */
export function readOfferSpots(raw: string | null | undefined): string[] | null {
  if (raw == null || raw === "") return null;
  try {
    const list = cleanSpots(JSON.parse(raw));
    return list.length ? list : null;
  } catch {
    return null;
  }
}

/** The coupon a buyer took with an HQ store's checkout (111D S4), once the order was paid. */
export interface IklanBumpCoupon {
  code: string;
  /** "RM10" or "15%". */
  label: string;
  productName: string;
  storeName: string;
  /** The advertiser's product page with the code already on. */
  url: string;
}

/** What a Manual campaign may pick; email rides with Advantage+ only. */
export const MANUAL_PLACEMENTS = IKLAN_PLACEMENTS.filter((p) => p !== "emel");

/**
 * imp: ≥50% on screen for 1 s · click: the card was tapped · view: its sheet
 * loaded (Meta's landing page view) · vc: the product showed in it (view content).
 */
export const IKLAN_EVENT_TYPES = ["imp", "click", "view", "vc"] as const;
export type IklanEventType = (typeof IKLAN_EVENT_TYPES)[number];
export const IKLAN_EVENTS_MAX = 40;

/** How many ad cards one HQ surface shows. */
export const FOR_YOU_SLOTS = 4;
/** A buyer can claim from the thank-you page for this long after paying. */
export const CLAIM_WINDOW_HOURS = 72;

/** How long after paying an order still opens a placement: only the thank-you page runs out. */
export function claimWindowFor(placement: IklanPlacement): number | null {
  return placement === "lepasbeli" ? CLAIM_WINDOW_HOURS : null;
}

/** What an advertised product sells, so HQ can keep a direct competitor off a given HQ store. */
export const IKLAN_CATEGORIES = {
  keluarga: "Keluarga & ibu bapa",
  "belajar-kanak": "Belajar kanak-kanak (app, kad, lembaran)",
  bisnes: "Alat & template bisnes",
  "kursus-bisnes": "Kursus & coaching bisnes",
  kecantikan: "Kecantikan & penjagaan diri",
  makanan: "Makanan & resepi",
  fesyen: "Fesyen",
  lain: "Lain-lain",
} as const;
export type IklanCategory = keyof typeof IKLAN_CATEGORIES;
export const IKLAN_CATEGORY_KEYS = Object.keys(IKLAN_CATEGORIES) as IklanCategory[];

export interface IklanSettings {
  /** Stores whose surfaces carry ads. Only HQ's own stores. */
  hqStores: string[];
  /** Share of HQ orders that see no ads, kept to measure the lift (0–20). */
  holdoutPct: number;
  /** Per HQ store: categories never shown there (direct competitors). */
  blocks: Record<string, IklanCategory[]>;
  /** Per placement: the least a paying ad pays per 1,000 impressions (RM). */
  floors: Partial<Record<IklanPlacement, number>>;
  /** Per placement: what HQ's own product earns per 1,000 impressions there (RM);
   *  a slot is never sold for less. */
  houseCpm: Partial<Record<IklanPlacement, number>>;
  /** Iklan Terurus (S14): the Meta ad account, Page and pixel HQ runs sellers' ads from, and its fee. */
  managed: ManagedSettings;
  /** HQ's own offer under a seller's support ticket (111D S6): never another seller's ad. */
  tiket: TiketOffer;
  /** The affiliate portal's ads are paid per affiliate who joins (RM each), never per impression. */
  affiliateCpa: number;
  /** What a tap on a sponsored product opens: its product page first, or straight onto the payment form. */
  clickOpens: IklanClickOpens;
  /** Which placements and spots run (113M S4). */
  placements: PlacementControl;
  /** How often one buyer sees the same ad (Monetize › Tetapan › Kekerapan). */
  frequency: FrequencyControl;
  /** The categories Pembantu Sellhiz shows its sellers (117C S7). */
  pembantuCategories: IklanCategory[];
  /** How sellers top up their ad wallet with CHIP (117L-5): the method shown first, and whether the other is offered. */
  topup: TopupControl;
  /**
   * Lelong dua mod (117L): of a product's rows, an Auto (GMV Max) row runs ahead
   * of the rest, else only the most valuable enters (Meta), the others waiting
   * as Sandaran; a product's rows learn together. Off: every row bids as before.
   */
  twoMode: boolean;
}

/**
 * Per buyer: an ad for a product they already paid for is never shown to them,
 * and an ad they kept passing over steps aside for them. Either way the next
 * ad in the auction takes the spot, so a card never goes empty.
 */
export interface FrequencyControl {
  hidePurchased: boolean;
  /** How far back a paid order counts, in days; 0 is any time. */
  purchasedDays: number;
  cap: boolean;
  /** Impressions without a tap before the ad steps aside for this buyer. */
  capViews: number;
  /** The window those impressions are counted over; a tap starts the count again. */
  capDays: number;
}

export const PURCHASED_DAYS_OPTIONS = [0, 90, 180, 365] as const;
export const CAP_VIEWS_MAX = 50;
/** Inside the viewer trail's 35-day retention. */
export const CAP_DAYS_MAX = 30;
export const DEFAULT_FREQUENCY: FrequencyControl = { hidePurchased: true, purchasedDays: 0, cap: true, capViews: 5, capDays: 7 };

export function readFrequency(raw: unknown): FrequencyControl {
  const o = (raw && typeof raw === "object" ? raw : {}) as Partial<FrequencyControl>;
  const int = (v: unknown, min: number, max: number, dflt: number) => {
    const n = Number(v);
    return v != null && Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : dflt;
  };
  const days = Number(o.purchasedDays);
  return {
    hidePurchased: o.hidePurchased !== false,
    purchasedDays: (PURCHASED_DAYS_OPTIONS as readonly number[]).includes(days) ? days : DEFAULT_FREQUENCY.purchasedDays,
    cap: o.cap !== false,
    capViews: int(o.capViews, 1, CAP_VIEWS_MAX, DEFAULT_FREQUENCY.capViews),
    capDays: int(o.capDays, 1, CAP_DAYS_MAX, DEFAULT_FREQUENCY.capDays),
  };
}

export type ViewerSkipReason = "purchased" | "capped";

/**
 * The ads a viewer passed over too often: impressions on the days after their
 * last tap on it (the trail is daily), at or above `capViews`.
 */
export function cappedOffers(rows: { offer_id: string; day: string; impressions: number; clicks: number }[], capViews: number): Set<string> {
  const byOffer = new Map<string, { lastClick: string; days: Map<string, number> }>();
  for (const r of rows) {
    const o = byOffer.get(r.offer_id) ?? { lastClick: "", days: new Map<string, number>() };
    if (Number(r.clicks) > 0 && r.day > o.lastClick) o.lastClick = r.day;
    o.days.set(r.day, (o.days.get(r.day) ?? 0) + Number(r.impressions));
    byOffer.set(r.offer_id, o);
  }
  const out = new Set<string>();
  for (const [offerId, o] of byOffer) {
    let since = 0;
    for (const [day, n] of o.days) if (day > o.lastClick) since += n;
    if (since >= capViews) out.add(offerId);
  }
  return out;
}

/** The products a buyer paid for, from their orders (single product or cart lines). */
export function purchasedProducts(orders: { product_id: string | null; order_items?: { product_id: string | null }[] | null }[]): Set<string> {
  const out = new Set<string>();
  for (const o of orders) {
    if (o.product_id) out.add(o.product_id);
    for (const it of o.order_items ?? []) if (it.product_id) out.add(it.product_id);
  }
  return out;
}

/** produk: the product page, with Beli to its checkout (as Shopee and Amazon open a sponsored product). checkout: the payment form at once. */
export const IKLAN_CLICK_OPENS = ["produk", "checkout"] as const;
export type IklanClickOpens = (typeof IKLAN_CLICK_OPENS)[number];
export const DEFAULT_CLICK_OPENS: IklanClickOpens = "produk";

/**
 * What an ad is for (117C). jualan: the engine looks for people who buy, as
 * every ad before. trafik: people who tap through to the seller's own link.
 * mesej: people who start a chat with the store.
 */
export const IKLAN_OBJECTIVES = ["jualan", "trafik", "mesej"] as const;
export type IklanObjective = (typeof IKLAN_OBJECTIVES)[number];

/** Where a tap on a Jualan ad goes: chosen per ad, or left to the engine (auto). */
export const IKLAN_DESTINATIONS = ["auto", "produk", "checkout", "salespage"] as const;
export type IklanDestination = (typeof IKLAN_DESTINATIONS)[number];
/** What a tap opens, once resolved: the destinations a sheet can show. */
export type IklanOpens = Exclude<IklanDestination, "auto">;

/** Iklan Terurus (Meta → FYP awam) is kept but out of sight until HQ uses it again. */
export const TERURUS_VISIBLE = false;

/** A Trafik ad's button (117C S3). */
export const TRAFIK_CTAS = { whatsapp: "WhatsApp", ketahui: "Ketahui lagi", daftar: "Daftar", beli: "Beli" } as const;
export type TrafikCta = keyof typeof TRAFIK_CTAS;
export const TRAFIK_CTA_KEYS = Object.keys(TRAFIK_CTAS) as TrafikCta[];
export const TRAFIK_HEADLINE_MAX = 40;
export const TRAFIK_BODY_MAX = 90;
export const TRAFIK_WA_TEXT_MAX = 200;

/**
 * Where a Trafik or Mesej ad may run: the card spots, which can show a
 * headline and a button instead of a product and its price, or open the shop
 * chat. Never a feed's video, a checkout's coupon bump, the affiliate portal,
 * a class room or an email.
 */
export const CARD_PLACEMENTS: readonly string[] = ["lepasbeli", "jejak", "akaun", "lms", "rakaman", "komuniti", "jelajah", "kedai", "pembantu"];

/** A Trafik ad has no Sellhiz product: its offer stands on this id instead (iklan_offers.product_id is unique and required). */
export const linkProductId = (campaignId: string) => `link:${campaignId}`;
export const isLinkProduct = (productId: string) => productId.startsWith("link:");

/** wa.me with the seller's number and the opening message already typed. */
export function waLink(number: string, text: string): string {
  const digits = number.replace(/\D/g, "").replace(/^0/, "60");
  return `https://wa.me/${digits}${text.trim() ? `?text=${encodeURIComponent(text.trim())}` : ""}`;
}

/** How a card names where its link goes: "WhatsApp", or the site's host. */
export function linkDomain(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    return host === "wa.me" || host === "api.whatsapp.com" ? "WhatsApp" : host;
  } catch {
    return "";
  }
}

export function readDestination(raw: unknown): IklanDestination | null {
  return typeof raw === "string" && (IKLAN_DESTINATIONS as readonly string[]).includes(raw) ? (raw as IklanDestination) : null;
}

/**
 * What a tap on one card opens. The ad's own choice wins over HQ's switch; a
 * sales page the product does not have falls back to the product page.
 */
export function resolveOpens(destination: IklanDestination | null, hqDefault: IklanClickOpens, hasSalesPage: boolean): IklanOpens {
  const d = destination && destination !== "auto" ? destination : hqDefault;
  return d === "salespage" && !hasSalesPage ? "produk" : d;
}

/** Placements whose viewer is a buyer HQ already knows: their details fill the form, so the form comes first. */
const KNOWN_BUYER_PLACEMENTS: readonly string[] = ["lepasbeli", "jejak", "testimoni", "sijil", "akaun", "lms", "rakaman", "kelas", "emel"];
/** Placements read at leisure, where the long pitch has room. */
const READING_PLACEMENTS: readonly string[] = ["komuniti", "pembantu"];

/** Where Auto starts before an ad has numbers of its own: the form for a known buyer, the pitch for a reader, the product for a stranger. */
export function startDestination(placement: string, hasSalesPage: boolean): IklanOpens {
  if (KNOWN_BUYER_PLACEMENTS.includes(placement)) return "checkout";
  if (READING_PLACEMENTS.includes(placement) && hasSalesPage) return "salespage";
  return "produk";
}

export interface DestStats {
  clicks: number;
  purchases: number;
}

/** Share of taps Auto keeps sending to the destinations not winning, so it never stops learning. */
export const DEST_EXPLORE_PCT = 10;
/** How many taps of its own a destination needs before its record outweighs where Auto started. */
const DEST_PRIOR_CLICKS = 20;

/**
 * Auto (dinamik): the destination for one tap. Each destination's purchases per
 * tap in this placement, starting from a small lead for `startDestination`;
 * the best one wins, and `DEST_EXPLORE_PCT` of seeds go to another. The seed is
 * the viewer, the ad and the day, so a buyer sees the same destination all day.
 */
export function pickDestination(args: { placement: string; hasSalesPage: boolean; stats: Partial<Record<IklanOpens, DestStats>>; seed: number }): IklanOpens {
  const arms: IklanOpens[] = args.hasSalesPage ? ["produk", "checkout", "salespage"] : ["produk", "checkout"];
  const start = startDestination(args.placement, args.hasSalesPage);
  const score = (a: IklanOpens) => {
    const s = args.stats[a] ?? { clicks: 0, purchases: 0 };
    const prior = a === start ? 0.06 : 0.04;
    return (s.purchases + prior * DEST_PRIOR_CLICKS) / (s.clicks + DEST_PRIOR_CLICKS);
  };
  const best = arms.reduce((b, a) => (score(a) > score(b) ? a : b), start);
  if (args.seed % 100 >= DEST_EXPLORE_PCT) return best;
  const others = arms.filter((a) => a !== best);
  return others[Math.floor(args.seed / 100) % others.length];
}

/** HQ's one line under a seller's support ticket: a house offer, not sold to advertisers. */
export interface TiketOffer {
  on: boolean;
  /** Bold lead, e.g. "Iklan Terurus". */
  title: string;
  /** The sentence after it. */
  text: string;
  /** Where "Ketahui" goes: an https:// address or a path on the app ("/dashboard/iklan"). */
  href: string;
}

export const TIKET_TEXT_MAX = 140;
export const DEFAULT_TIKET: TiketOffer = { on: false, title: "", text: "", href: "" };

/** A link HQ may point a ticket's offer at: https, or a path on the app. Anything else is dropped. */
export function tiketHref(raw: unknown): string {
  if (typeof raw !== "string") return "";
  const v = raw.trim().slice(0, 400);
  if (/^\/(?!\/)[^\s]*$/.test(v)) return v;
  try {
    const u = new URL(v);
    return u.protocol === "https:" ? u.toString() : "";
  } catch {
    return "";
  }
}

export function readTiket(raw: unknown): TiketOffer {
  const t = (raw && typeof raw === "object" ? raw : {}) as Partial<TiketOffer>;
  const clean = (v: unknown, max: number) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, max) : "");
  const next = { on: t.on === true, title: clean(t.title, 40), text: clean(t.text, TIKET_TEXT_MAX), href: tiketHref(t.href) };
  // An offer with nowhere to go, or nothing to say, is off.
  return { ...next, on: next.on && !!next.href && !!(next.title || next.text) };
}

/** CHIP methods an ad top-up opens straight on (117L-5): the QR to scan, or the bank list. */
export const TOPUP_METHODS = ["duitnow_qr", "fpx"] as const;
export type TopupMethod = (typeof TOPUP_METHODS)[number];
export const TOPUP_LABEL: Record<TopupMethod, string> = { duitnow_qr: "DuitNow QR", fpx: "FPX" };
export interface TopupControl {
  /** Shown first. */
  method: TopupMethod;
  /** The seller may switch to the other method. */
  allowOther: boolean;
}
export const DEFAULT_TOPUP: TopupControl = { method: "duitnow_qr", allowOther: true };

function readTopup(raw: unknown): TopupControl {
  const t = (raw && typeof raw === "object" ? raw : {}) as Partial<TopupControl>;
  return { method: t.method === "fpx" ? "fpx" : "duitnow_qr", allowOther: t.allowOther !== false };
}

/** What a seller may pay a top-up with: the default, and the other when HQ allows it. */
export function topupMethodFor(c: TopupControl, asked: TopupMethod | undefined): TopupMethod {
  return asked && (asked === c.method || c.allowOther) ? asked : c.method;
}

export interface ManagedSettings {
  /** Digits only, without "act_". */
  adAccountId: string;
  pageId: string;
  pixelId: string;
  /** Added to each ringgit of Meta spend a seller is billed (25 = 25%). */
  feePct: number;
}

export const DEFAULT_FLOOR_CPM = 20;
export const MANAGED_FEE_PCT = 25;
/** Meta needs enough budget to learn: an Iklan Terurus campaign spends at least this a day. */
export const MANAGED_MIN_DAILY = 30;
/** Below this much spend + fee, a campaign's ROAS says too little to pause it on. */
export const MANAGED_ROAS_MIN_SPEND = 30;

const DEFAULT_MANAGED: ManagedSettings = { adAccountId: "", pageId: "", pixelId: "", feePct: MANAGED_FEE_PCT };

export const DEFAULT_IKLAN_SETTINGS: IklanSettings = {
  hqStores: [],
  holdoutPct: 10,
  blocks: {},
  floors: {},
  houseCpm: {},
  managed: DEFAULT_MANAGED,
  tiket: DEFAULT_TIKET,
  affiliateCpa: DEFAULT_AFFILIATE_CPA,
  clickOpens: DEFAULT_CLICK_OPENS,
  placements: DEFAULT_PLACEMENT_CONTROL,
  frequency: DEFAULT_FREQUENCY,
  pembantuCategories: [...PEMBANTU_DEFAULT_CATEGORIES],
  topup: DEFAULT_TOPUP,
  twoMode: false,
};

/** HQ's view of Iklan Terurus: each seller's Meta campaign and what it was billed. */
export interface ManagedOverview {
  days: number;
  totals: { spend: number; fee: number };
  campaigns: {
    campaignId: string;
    name: string;
    storeName: string;
    state: "pending" | "running" | "paused" | "failed";
    pausedReason: string | null;
    error: string | null;
    metaCampaignId: string | null;
    ads: number;
    spend: number;
    fee: number;
  }[];
}

/** Iklan Terurus can take campaigns: HQ named its Meta account, Page and pixel, and FYP awam is on. */
export function managedOpen(s: IklanSettings): boolean {
  return !!(s.managed.adAccountId && s.managed.pageId && s.managed.pixelId && s.hqStores.includes(AWAM_HOST));
}

/** What a seller is charged for Meta spend, fee included, in sen. */
export function managedChargeCents(spendCents: number, feePct: number): { spend: number; fee: number } {
  return { spend: spendCents, fee: Math.round((spendCents * feePct) / 100) };
}

function readPerPlacement(raw: unknown): Partial<Record<IklanPlacement, number>> {
  const out: Partial<Record<IklanPlacement, number>> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw)) {
    const n = Number(v);
    if ((IKLAN_PLACEMENTS as readonly string[]).includes(k) && Number.isFinite(n) && n >= 0) out[k as IklanPlacement] = Math.round(n * 100) / 100;
  }
  return out;
}

export function floorFor(s: IklanSettings, placement: IklanPlacement): number {
  return s.floors[placement] ?? DEFAULT_FLOOR_CPM;
}

export function houseFor(s: IklanSettings, placement: IklanPlacement): number {
  return s.houseCpm[placement] ?? 0;
}

export function readIklanSettings(raw: unknown): IklanSettings {
  const o = (raw && typeof raw === "object" ? raw : {}) as Partial<IklanSettings>;
  const hqStores = Array.isArray(o.hqStores) ? o.hqStores.filter((s): s is string => typeof s === "string") : [];
  const pct = Number(o.holdoutPct);
  const blocks: Record<string, IklanCategory[]> = {};
  if (o.blocks && typeof o.blocks === "object") {
    for (const [store, cats] of Object.entries(o.blocks)) {
      if (!Array.isArray(cats)) continue;
      const clean = [...new Set(cats.filter((c): c is IklanCategory => (IKLAN_CATEGORY_KEYS as string[]).includes(c)))];
      if (clean.length) blocks[store] = clean;
    }
  }
  return {
    hqStores,
    holdoutPct: Number.isFinite(pct) ? Math.min(20, Math.max(0, Math.round(pct))) : 10,
    blocks,
    floors: readPerPlacement(o.floors),
    houseCpm: readPerPlacement(o.houseCpm),
    managed: readManaged(o.managed),
    tiket: readTiket(o.tiket),
    affiliateCpa: readCpa(o.affiliateCpa),
    clickOpens: o.clickOpens === "checkout" ? "checkout" : DEFAULT_CLICK_OPENS,
    placements: readPlacementControl(o.placements),
    frequency: readFrequency(o.frequency),
    pembantuCategories: readCategories(o.pembantuCategories, PEMBANTU_DEFAULT_CATEGORIES),
    topup: readTopup(o.topup),
    twoMode: o.twoMode === true,
  };
}

/** A list of categories as settings keep it; none given keeps the default, an empty list switches all off. */
function readCategories(raw: unknown, fallback: readonly IklanCategory[]): IklanCategory[] {
  if (!Array.isArray(raw)) return [...fallback];
  return [...new Set(raw.filter((c): c is IklanCategory => (IKLAN_CATEGORY_KEYS as string[]).includes(c)))];
}

function readCpa(raw: unknown): number {
  const n = Number(raw);
  return raw != null && Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n * 100) / 100)) : DEFAULT_AFFILIATE_CPA;
}

function readManaged(raw: unknown): ManagedSettings {
  const m = (raw && typeof raw === "object" ? raw : {}) as Partial<ManagedSettings>;
  const digits = (v: unknown) => (typeof v === "string" ? v.replace(/^act_/, "").replace(/\D/g, "").slice(0, 32) : "");
  const fee = Number(m.feePct);
  return {
    adAccountId: digits(m.adAccountId),
    pageId: digits(m.pageId),
    pixelId: digits(m.pixelId),
    feePct: Number.isFinite(fee) ? Math.min(100, Math.max(0, Math.round(fee * 10) / 10)) : MANAGED_FEE_PCT,
  };
}

/** True when this HQ store keeps the offer's category off its surfaces. */
export function blockedAt(settings: IklanSettings, hostStoreId: string, category: string | null): boolean {
  if (!category) return false;
  return (settings.blocks[hostStoreId] ?? []).includes(category as IklanCategory);
}

export interface LiftArm {
  orders: number;
  converted: number;
  rate: number;
}

export interface LiftReport {
  test: LiftArm;
  control: LiftArm;
  /** Extra second purchases per HQ buyer, test over control, in points; null without both arms. */
  liftPts: number | null;
}

/**
 * Did seeing ads make HQ buyers buy again from opted-in sellers? Each HQ order
 * seen on a placement counts once in its arm; it converts when the same buyer
 * paid another store within `windowHours` of that order.
 */
export function liftFromRows(
  seen: { orderId: string; arm: "test" | "control"; email: string; paidAt: string }[],
  sellerBuys: { email: string; paidAt: string }[],
  windowHours = CLAIM_WINDOW_HOURS,
): LiftReport {
  const buysBy = new Map<string, number[]>();
  for (const b of sellerBuys) {
    const list = buysBy.get(b.email) ?? [];
    list.push(new Date(b.paidAt).getTime());
    buysBy.set(b.email, list);
  }
  const arm = (): LiftArm => ({ orders: 0, converted: 0, rate: 0 });
  const out = { test: arm(), control: arm() };
  for (const s of seen) {
    const a = out[s.arm];
    a.orders += 1;
    const t0 = new Date(s.paidAt).getTime();
    if ((buysBy.get(s.email) ?? []).some((t) => t > t0 && t - t0 <= windowHours * 3600_000)) a.converted += 1;
  }
  for (const a of [out.test, out.control]) a.rate = a.orders ? a.converted / a.orders : 0;
  const liftPts = out.test.orders && out.control.orders ? Math.round((out.test.rate - out.control.rate) * 1000) / 10 : null;
  return { ...out, liftPts };
}

/** FNV-1a, 32-bit. Stable across Worker and browser. */
export function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** The same order always lands in the same arm. */
export function holdoutArm(orderId: string, pct: number): "test" | "control" {
  return hash32(`iklan:${orderId}`) % 100 < pct ? "control" : "test";
}

/** Malaysian calendar day of `now`, as YYYY-MM-DD. */
export function mytDay(now: Date): string {
  return new Date(now.getTime() + 8 * 3600_000).toISOString().slice(0, 10);
}

/** The query a destination puts on the product page: the payment form, or its sales page open. */
function opensQuery(opens: IklanOpens): Record<string, string> {
  return opens === "checkout" ? { [DIRECT_CHECKOUT_PARAM]: "1" } : opens === "salespage" ? { [SALES_PAGE_PARAM]: "1" } : {};
}

/** The seller's product page, opened where the ad says (`opens`), tagged for attribution. */
export function iklanProductUrl(
  origin: string,
  productSlug: string,
  opts: { placement: string; offerId: string; code?: string | null; opens: IklanOpens; ticket?: string | null },
): string {
  const q = new URLSearchParams(opensQuery(opts.opens));
  if (opts.code) q.set("shz_cpn", opts.code);
  if (opts.ticket) q.set(AD_TICKET_PARAM, opts.ticket);
  q.set("utm_source", "sellhiz_iklan");
  q.set("utm_medium", opts.placement);
  q.set("utm_campaign", opts.offerId);
  return `${origin.replace(/\/+$/, "")}/product/${encodeURIComponent(productSlug)}?${q.toString()}`;
}

export interface ForYouItem {
  offerId: string;
  productId: string;
  name: string;
  image: string | null;
  price: number;
  currency: string;
  storeName: string;
  url: string;
  coupon: { type: "fixed" | "percent"; value: number; minSubtotal: number } | null;
  claimedCode: string | null;
  /** 127B: the signed click ticket that carries the coupon to checkout, when the card has one. */
  ticket?: string;
  /** 127B: the offer has a coupon this viewer cannot have today, and why; `coupon` is then null. */
  couponOut?: "budget_exhausted" | "already_used";
  /** The affiliate portal only (111D S7): the advertiser's store, to join. */
  storeId?: string;
  /** The affiliate portal only: what one sale of this product pays an affiliate. */
  commission?: { type: "percent" | "fixed"; rate: number; perSale: number } | null;
  /** The spots of this placement the ad is kept to (its Manual placements); absent: any. */
  slots?: IklanSlot[];
  /** What a tap on this card opens (117C); absent: the page's `opens`. */
  opens?: IklanOpens;
  /** A Trafik ad (117C S3): its own words and button, and where the link goes; `name` is its headline, `url` the counted redirect. */
  link?: { body: string; cta: TrafikCta; domain: string };
  /** A Mesej ad (117C S4): a tap opens the shop chat instead of the product. */
  chat?: true;
}

export interface ForYouResponse {
  show: boolean;
  /** The paid HQ order the viewer stands for: what the page's taps and claims name. */
  orderId?: string;
  /** The HQ store the page belongs to; rides into the order as the ad's host. */
  hostStoreId?: string;
  /** Who is looking, for the sheet's checkout (their own name and email). */
  buyer?: { name: string; email: string };
  arm?: "test" | "control";
  /** Coupons on the thank-you page are claimable until then; null elsewhere. */
  claimUntil?: string | null;
  items: ForYouItem[];
  /** On a public surface: the signed viewer pass this page's impressions and taps name. */
  pass?: string;
  /** In a feed: each winning ad's FYP video, to play between the store's own. */
  videos?: SponsoredVideo[];
  /** What a tap opens (Monetize › Tetapan); absent means the product page. */
  opens?: IklanClickOpens;
  /** This placement's spots HQ switched off: the page leaves them empty. */
  off?: IklanSlot[];
  /** Pembantu Sellhiz: this week's sponsored message in its inbox (117C S7). */
  ditaja?: DitajaMessage | null;
}

/** Mesej Ditaja: one ad, shown as a message in Pembantu's inbox, billed when the seller opens it. */
export interface DitajaMessage {
  id: string;
  item: ForYouItem;
  opened: boolean;
}

/** An advertiser's FYP video, played in an HQ store's feed as a sponsored card. */
export interface SponsoredVideo {
  offerId: string;
  storeName: string;
  /** FYP awam: the video the visitor's ad link named, played first and never charged as an impression. */
  landing?: true;
  video: import("./types").PublicFypVideo;
  post: import("./types").PublicFypPost;
}

/** Price after the coupon, never below zero. */
/**
 * What one of the product costs with the ad's coupon. A coupon whose minimum
 * spend one unit does not reach takes nothing off here: the card shows the
 * price a buyer of one will pay, and the coupon's terms beside it.
 */
export function priceAfterCoupon(price: number, coupon: ForYouItem["coupon"]): number {
  if (!coupon || price < (coupon.minSubtotal ?? 0)) return price;
  const off = coupon.type === "fixed" ? coupon.value : (price * coupon.value) / 100;
  return Math.max(0, Math.round((price - off) * 100) / 100);
}

/** The ad behind a checkout, carried on its address (iko, ikp, ikh, iks: the spot it was tapped in, ikd: what the tap opened). */
export interface IklanTouch {
  o: string;
  p: IklanPlacement;
  h: string;
  s?: IklanSlot;
  d?: IklanOpens;
}

const OPENS: readonly string[] = ["produk", "checkout", "salespage"];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function iklanTouchFromSearch(search: string): IklanTouch | null {
  const q = new URLSearchParams(search);
  const o = q.get("iko") ?? "";
  const p = q.get("ikp") ?? "";
  const h = q.get("ikh") ?? "";
  if (!UUID_RE.test(o) || !UUID_RE.test(h)) return null;
  if (!(IKLAN_PLACEMENTS as readonly string[]).includes(p)) return null;
  const s = q.get("iks") ?? "";
  const d = q.get("ikd") ?? "";
  return {
    o,
    p: p as IklanPlacement,
    h,
    ...((IKLAN_SLOTS as readonly string[]).includes(s) ? { s: s as IklanSlot } : {}),
    ...(OPENS.includes(d) ? { d: d as IklanOpens } : {}),
  };
}

/** The seller's product, on the HQ page's own origin, opened in a sheet: its
 *  product page, payment form or sales page (`opens`). Same origin is what lets the sheet frame it. */
export function iklanSheetPath(
  offerId: string,
  opts: { placement: IklanPlacement; hostStoreId: string; code?: string | null; opens: IklanOpens; slot?: IklanSlot; ticket?: string | null },
): string {
  const q = new URLSearchParams({ ...opensQuery(opts.opens), shz_sheet: "1", iko: offerId, ikp: opts.placement, ikh: opts.hostStoreId, ikd: opts.opens });
  if (opts.slot) q.set("iks", opts.slot);
  if (opts.code) q.set("shz_cpn", opts.code);
  if (opts.ticket) q.set(AD_TICKET_PARAM, opts.ticket);
  return `/iklan/p/${encodeURIComponent(offerId)}?${q.toString()}`;
}

/** Buyer details the HQ page hands the sheet's checkout (same tab, same origin). */
export const IKLAN_PREFILL_KEY = "shz_iklan_prefill";
/** What the product page in an ad's sheet tells the HQ page once it shows the product (view content). */
export const IKLAN_VC_MESSAGE = "shz_iklan_vc";

/** An advertiser's rung on HQ's spend ladder, by what it spent on ads over 30 days (RM). */
export const SPEND_TIERS = [
  { key: "platinum", name: "Platinum", min: 5000 },
  { key: "emas", name: "Emas", min: 1000 },
  { key: "perak", name: "Perak", min: 300 },
  { key: "gangsa", name: "Gangsa", min: 50 },
  { key: "baru", name: "Baru", min: 0 },
] as const;
export type SpendTier = (typeof SPEND_TIERS)[number]["key"];

export function tierOf(spendRm: number): SpendTier {
  return (SPEND_TIERS.find((t) => spendRm >= t.min) ?? SPEND_TIERS[SPEND_TIERS.length - 1]).key;
}

/** HQ's Monetize overview: what HQ earned, what sellers spent and sold, by day and by advertiser. */
/** One bucket of Monetize's chart (121L): an hour, a day or a month, Malaysia time. */
export interface MonetizeOverviewPoint {
  /** "2026-10-19T14", "2026-10-19" or "2026-10". */
  b: string;
  cpm: number;
  managedFee: number;
  managedSpend: number;
  gmv: number;
  purchases: number;
}

export interface MonetizeOverview {
  days: number;
  julat: { from: string; to: string; prevFrom: string | null; prevTo: string | null; bucket: "hour" | "day" | "month"; earliest: string | null };
  totals: {
    /** HQ's income: CPM charged to sellers plus Iklan Terurus fees (RM). */
    hqRevenue: number;
    cpm: number;
    managedFee: number;
    /** Meta spend passed through to sellers (RM), not HQ's income. */
    managedSpend: number;
    /** What sellers paid in all (RM). */
    sellerCost: number;
    /** Sales credited to ads (RM): 7-day click, 1-day view. */
    gmv: number;
    purchases: number;
    /** Sales per ringgit sellers spent; null before any spend. */
    roas: number | null;
    advertisers: number;
  };
  /** The span just before, cut where the range has got to; null for Maksimum. */
  prevTotals: { hqRevenue: number; sellerCost: number; gmv: number; purchases: number; roas: number | null } | null;
  series: MonetizeOverviewPoint[];
  previous: MonetizeOverviewPoint[] | null;
  ladder: { storeId: string; storeName: string; cost: number; gmv: number; roas: number | null; tier: SpendTier }[];
  tiers: Record<SpendTier, number>;
  /** Where on each surface ads were seen and tapped, most-seen first. */
  slots: { placement: string; slot: string; impressions: number; clicks: number }[];
}

/** Monetize (operator console) shapes. */
export interface MonetizeStore {
  id: string;
  name: string;
  slug: string;
  hq: boolean;
  orders30: number;
  gmv30: number;
}

export interface MonetizeOffer extends AdvertiserWeb {
  id: string;
  storeId: string;
  storeName: string;
  productId: string;
  productName: string;
  productActive: boolean;
  price: number;
  currency: string;
  category: IklanCategory | null;
  coupon: { type: "fixed" | "percent"; value: number; minSubtotal: number; dailyBudget: number | null } | null;
  status: "active" | "paused";
  billing: "free" | "cpm";
  dailyBudget: number | null;
  /** The ad's own spots (Manual); null runs wherever HQ does (Auto). */
  placements: string[] | null;
  /** The campaign whose row this is; null: HQ's own row (117L: a product may hold both). */
  campaign: { id: string; name: string; kind: "auto" | "manual" } | null;
  impressions: number;
  clicks: number;
  claims: number;
  purchases: number;
  revenue: number;
  /** What the auction charged it, RM. */
  spend: number;
}

/**
 * Who an advertiser is and where it lives on the web, so HQ can tell apart
 * look-alike products sold by different stores.
 */
export interface AdvertiserWeb {
  /** The store is owned by a platform admin. */
  superadmin: boolean;
  /** The store carries HQ's ads (its HQ switch is on). */
  hq: boolean;
  /** Storefront: verified domain, else subdomain, else the apex /shop page. */
  storeUrl: string | null;
  /** The product's own public page. */
  productUrl: string | null;
}

export interface MonetizeProduct extends AdvertiserWeb {
  id: string;
  name: string;
  price: number;
  currency: string;
  storeId: string;
  storeName: string;
}

/** The advertiser picker's owner filter. */
export const MONETIZE_OWNERS = [
  ["all", "Semua"],
  ["superadmin", "Superadmin"],
  ["seller", "Penjual"],
] as const;
export type MonetizeOwner = (typeof MONETIZE_OWNERS)[number][0];

/** A storefront URL as a label: "kedai.sellhiz.win", or "sellhiz.win/shop/kedai". */
export function webHost(url: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return (u.host + u.pathname).replace(/\/+$/, "");
  } catch {
    return null;
  }
}

/** Superadmin stores' products first; the order is otherwise kept. */
export function superadminFirst<T extends { superadmin: boolean }>(rows: T[]): T[] {
  return [...rows.filter((r) => r.superadmin), ...rows.filter((r) => !r.superadmin)];
}

export interface MonetizeOfferInput {
  productId: string;
  category: IklanCategory;
  couponType: "fixed" | "percent" | null;
  couponValue: number | null;
  couponMinSubtotal: number;
  couponDailyBudget: number | null;
}

/** The advertiser's wallet as the seller dashboard shows it. */
export interface WalletView {
  balanceCents: number;
  ledger: { kind: string; amountCents: number; note: string | null; balanceAfterCents: number; createdAt: string }[];
  topupReady: boolean;
  /** Kredit iklan grants (117L-4), newest first: spent before the store's own cash. */
  credits: StoreCredit[];
  /** How a top-up is paid (117L-5), and the phone the last one gave CHIP. */
  topup: TopupControl & { phone: string | null };
}

/** A seller's campaign in HQ's list (Monetize › Pengiklan), for deleting one or many (117L-3). */
export interface MonetizeCampaign {
  id: string;
  storeId: string;
  storeName: string;
  kind: "auto" | "manual";
  /** Jualan, Trafik or Mesej (117C). */
  objective: IklanObjective;
  name: string;
  status: "review" | "active" | "rejected" | "ended";
  on: boolean;
  products: number;
  createdAt: string;
}

/** What a bulk delete or "Buat asal" did. */
export interface BulkResult {
  done: string[];
  /** Left as they were: an Auto whose product went to another Auto, an HQ row whose product HQ advertised again. */
  blocked: string[];
}

/** Monetize → Lelong: one HQ store's auction as it would run now. */
export interface AuctionView {
  placement: IklanPlacement;
  reserveCpm: number;
  floorCpm: number;
  houseCpm: number;
  rows: {
    offerId: string;
    productName: string;
    storeName: string;
    billing: "free" | "cpm";
    bidCents: number;
    pPurchase: number;
    quality: number;
    valueCpm: number;
    priceCpm: number;
    slot: number | null;
    out: "below_reserve" | "no_slot" | null;
    /** Whose row it is (117L): HQ's own, or a GMV Max or Manual campaign's. */
    source: AuctionRowSource;
  }[];
  skipped: { offerId: string; productName: string; storeName: string; reason: AuctionSkipReason }[];
  /** Lelong dua mod is on (117L). */
  twoMode: boolean;
}

export type AuctionRowSource = "hq" | "auto" | "manual";
export type AuctionSkipReason = "blocked" | "no_balance" | "budget_spent" | "campaign_off" | "targeting" | "purchased" | "capped" | "sandaran";
